export type GoogleConsentValue = "granted" | "denied";

export interface GoogleConsentState {
  ad_storage: GoogleConsentValue;
  ad_user_data: GoogleConsentValue;
  ad_personalization: GoogleConsentValue;
  analytics_storage: GoogleConsentValue;
}

type GtagCommand = "config" | "consent" | "event" | "js" | "set";
type EventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (
      command: GtagCommand,
      target: string | Date,
      params?: Record<string, unknown>,
    ) => void;
  }
}

// Overridable per-environment so staging/dev traffic doesn't pollute the
// production GA4 property. Falls back to the production tag for compatibility.
export const GOOGLE_TAG_ID =
  import.meta.env.VITE_GOOGLE_TAG_ID || "GT-KF8HG69R";
export const CONSENT_STORAGE_KEY = "bloxida-google-consent-v1";

export const deniedConsent: GoogleConsentState = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
};

export const grantedConsent: GoogleConsentState = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
};

let tagInjected = false;
export function initGoogleTag() {
  if (typeof window === "undefined" || tagInjected) return;
  tagInjected = true;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
  }

  try {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG_ID}`;
    script.onerror = () => {
      // AdBlocker / client-side block intercepted gracefully
    };
    document.head.appendChild(script);

    window.gtag("js", new Date());
    window.gtag("config", GOOGLE_TAG_ID, { send_page_view: false });
  } catch {
    // Fallback when script creation or DOM insertion is restricted
  }
}

export function updateGoogleConsent(consent: GoogleConsentState) {
  if (typeof window === "undefined" || typeof window.gtag !== "function")
    return;
  try {
    window.gtag(
      "consent",
      "update",
      consent as unknown as Record<string, unknown>,
    );
  } catch {
    // Graceful fallback when blocked by client adblocker
  }
}

/**
 * Read the user's stored consent decision (if any). Returns null when the user
 * has not yet interacted with the consent banner — callers should treat null as
 * "consent denied" for privacy-sensitive operations.
 */
export function readStoredGoogleConsent(): GoogleConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GoogleConsentState>;
    if (
      (parsed.analytics_storage === "granted" ||
        parsed.analytics_storage === "denied") &&
      (parsed.ad_storage === "granted" || parsed.ad_storage === "denied") &&
      (parsed.ad_user_data === "granted" || parsed.ad_user_data === "denied") &&
      (parsed.ad_personalization === "granted" ||
        parsed.ad_personalization === "denied")
    ) {
      return parsed as GoogleConsentState;
    }
  } catch {
    // corrupted storage → treat as no consent
  }
  return null;
}

/** True iff the user has explicitly granted analytics consent. */
export function hasAnalyticsConsent(): boolean {
  return readStoredGoogleConsent()?.analytics_storage === "granted";
}

export function sendPageView({
  title,
  path,
  location,
  referrer,
  timeZone,
  region,
  language,
}: {
  title: string;
  path: string;
  location: string;
  referrer?: string;
  timeZone?: string;
  region?: string;
  language?: string;
}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function")
    return;

  try {
    window.gtag("event", "page_view", {
      page_title: title,
      page_path: path,
      page_location: location,
      ...(referrer ? { page_referrer: referrer } : {}),
      ...(timeZone ? { user_timezone: timeZone } : {}),
      ...(region ? { user_region: region } : {}),
      ...(language ? { page_language: language } : {}),
    });
  } catch {
    // Graceful fallback when blocked by client
  }
}

export function sendGoogleEvent(eventName: string, params: EventParams = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function")
    return;

  try {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== "",
      ),
    );

    window.gtag("event", eventName, cleanParams);
  } catch {
    // Graceful fallback when blocked by client
  }
}
